import { z } from 'zod';
import type { Permission } from './permissions';

/**
 * Organizations (docs/organizations-design.md).
 *
 * Every account belongs to exactly one organization -- a `Party` in the
 * database, called an organization everywhere a person reads it. The client's
 * own staff belong to the host organization; a supplier's people belong to the
 * supplier. A role says what somebody may do; their organization says whose
 * rows they may do it to.
 *
 * This file holds the rules both sides of the wire must agree on: what an
 * external member can ever hold, and how far each administrative act reaches
 * for a given permission set. The API enforces them and the web renders from
 * them, so the two cannot drift apart.
 */

/** Who an account belongs to, as the session and the user list carry it. */
export const membershipSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** The client's own staff. Everybody else is an external member. */
  isHost: z.boolean(),
});
export type Membership = z.infer<typeof membershipSchema>;

/**
 * An organization as a record names it: who a task is for, who issued a
 * revision, whom an attendee represented (docs/organizations-design.md
 * section 9.1). The name travels with the id so a reader without `party.read`
 * -- who cannot list the register -- still sees who it is.
 */
export const organizationRefSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});
export type OrganizationRef = z.infer<typeof organizationRefSchema>;

/**
 * The most a member of any organization but the host can hold.
 *
 * Applied in `SessionService.resolve`, after the module intersection, so it
 * holds whatever roles somebody was granted: an administrator who gives the
 * `administrator` role to a haulier by mistake has given them this list and
 * nothing more. The grant is refused as well (`party.role_not_external`), so
 * the role list never claims more than the session will honour -- but the
 * ceiling is what the security rests on.
 *
 * Every permission here is one whose routes are marked `@ExternalAccess()` and
 * scope their rows by membership. Adding one means doing both, and
 * `organizations.integration.test.ts` is where that is proved.
 */
export const EXTERNAL_MEMBER_PERMISSIONS = [
  'fleet.read',
  'fleet.manage',
  'fleet.disclose',
  /* Only the agreements their organization supplies. Not managing them: an
     agreement is the authority's record of its own procurement. */
  'fleet.agreement.read',
  /* Their own dashboards. Shared views are the client's and are not listed. */
  'view.manage_own',
  'party.members.read',
  'party.members.manage',
  'party.members.invite',
  /* The project modules' reads (docs/organizations-design.md section 9.2),
     each honoured only on the routes that scope by membership: a supplier's
     own contracts and their positions, the documents it issued, the meetings
     it attended, the tasks it is responsible for. */
  'cost.read',
  'document.read',
  'meeting.read',
  'schedule.read',
  /* Seeing people outside their own organization, through the directory and
     the names on the records they read (section 10). */
  'directory.host',
  'directory.all',
] as const satisfies readonly Permission[];

const EXTERNAL_MEMBER_SET: ReadonlySet<Permission> = new Set(EXTERNAL_MEMBER_PERMISSIONS);

export function isExternalMemberPermission(permission: Permission): boolean {
  return EXTERNAL_MEMBER_SET.has(permission);
}

/**
 * The permissions in `permissions` an external member may not hold, in order.
 * Empty exactly when a role holding them may be granted to one.
 */
export function beyondExternalCeiling(permissions: readonly Permission[]): Permission[] {
  return permissions.filter((permission) => !EXTERNAL_MEMBER_SET.has(permission));
}

/**
 * The dashboard components an external member may place on their own views.
 *
 * Only those whose data comes from routes opened to them, so the picker offers
 * a supplier what would work rather than tiles that could only show a refusal.
 * The two fleet tiles drawn from the summary are here because the summary is
 * scoped to the reader's own vehicles; the data-quality and compliance tiles
 * are not, because those figures are about the client's procurement.
 */
export const EXTERNAL_MEMBER_COMPONENTS = [
  'markdown-note',
  'fleet-energy-mix',
  'fleet-verification',
] as const;

export function isExternalMemberComponent(type: string): boolean {
  return (EXTERNAL_MEMBER_COMPONENTS as readonly string[]).includes(type);
}

/* -------------------------------------------------------- administration */

/**
 * The administrative acts on accounts, each of which can reach either the
 * whole deployment or only the actor's own organization
 * (docs/organizations-design.md section 3.5).
 *
 * `roles` and `manage` share the organization-scoped permission on purpose. A
 * lead who may disable one of their people but not change what that person
 * can do would be a distinction nobody asked for; the one that was asked for
 * -- managing without inviting -- is `invite`, which has its own.
 */
export type AdministrationAct = 'read' | 'manage' | 'roles' | 'invite';

export const ADMINISTRATION_PERMISSIONS = {
  read: { deployment: 'user.read', organization: 'party.members.read' },
  manage: { deployment: 'user.manage', organization: 'party.members.manage' },
  roles: { deployment: 'role.manage', organization: 'party.members.manage' },
  invite: { deployment: 'user.manage', organization: 'party.members.invite' },
} as const satisfies Record<
  AdministrationAct,
  { deployment: Permission; organization: Permission }
>;

export type AdministrationReach = 'deployment' | 'organization';

/**
 * How far `held` reaches for `act`: every account, only the actor's own
 * organization's, or none. The deployment-wide right wins when both are held.
 *
 * An external member can never reach the deployment: none of the
 * deployment-wide rights are inside their ceiling.
 */
export function administrationReach(
  held: readonly Permission[],
  act: AdministrationAct,
): AdministrationReach | null {
  const rule = ADMINISTRATION_PERMISSIONS[act];
  if (held.includes(rule.deployment)) return 'deployment';
  if (held.includes(rule.organization)) return 'organization';
  return null;
}

/** Either permission that opens `act` at some reach -- what the route guard admits. */
export function administrationPermissions(act: AdministrationAct): [Permission, Permission] {
  const rule = ADMINISTRATION_PERMISSIONS[act];
  return [rule.deployment, rule.organization];
}

/**
 * Moving an account between organizations.
 *
 * Both rights an account's access is made of, and at deployment reach only.
 * A move changes which rows every role the person holds applies to, and a
 * move into the host lifts every restriction on them -- so it is not
 * delegable, and `user.manage` alone (which cannot touch roles) is not
 * enough.
 */
export const MEMBERSHIP_CHANGE_PERMISSIONS = [
  'user.manage',
  'role.manage',
] as const satisfies readonly Permission[];
